import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import PC components
import PCLayout from './PCLayout';
import { Me, Education, Hobbies } from './AboutMe';

const PCView = ({ isDarkMode, toggleTheme }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial active section based on location state
    const getInitialSection = () => {
        if (location.state && location.state.targetSection) {
            return location.state.targetSection.toLowerCase();
        }
        return 'me';
    };

    // State to track active section
    const [activeSection, setActiveSection] = useState(getInitialSection());

    // Flag to prevent unnecessary updates when programmatically scrolling
    const isScrollingProgrammatically = useRef(false);

    // Cooldown timer to prevent active indicator flickering
    const cooldownActiveRef = useRef(false);
    const cooldownTimerRef = useRef(null);

    // Container ref for scroll observation
    const contentMainRef = useRef(null);

    // Refs for each section
    const meRef = useRef(null);
    const educationRef = useRef(null);
    const hobbiesRef = useRef(null);

    // Set cooldown for the active indicator
    const setCooldown = () => {
        // Set cooldown active
        cooldownActiveRef.current = true;

        // Clear any existing cooldown timer
        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current);
        }

        // Set new cooldown timer
        cooldownTimerRef.current = setTimeout(() => {
            cooldownActiveRef.current = false;
        }, 500); // 0.5 second cooldown
    };

    // Handle menu item click
    const handleMenuItemClick = (sectionId) => {
        // Professional sections navigate to /prof route with state containing target section
        const professionalSections = ['experience', 'research', 'skills', 'more'];
        if (professionalSections.includes(sectionId.toLowerCase())) {
            navigate('/prof', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        // Miscellaneous sections navigate to /misc route with state containing target section
        const miscSections = ['accolades', 'projects', 'blog'];
        if (miscSections.includes(sectionId.toLowerCase())) {
            navigate('/misc', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        const sectionRefs = {
            'me': meRef,
            'education': educationRef,
            'hobbies': hobbiesRef
        };

        const ref = sectionRefs[sectionId.toLowerCase()];
        if (ref && ref.current) {
            // Set flag to prevent change from triggering another scroll
            isScrollingProgrammatically.current = true;

            // Set the active section immediately
            setActiveSection(sectionId.toLowerCase());

            // Activate cooldown to prevent flickering
            setCooldown();

            // Scroll to section
            ref.current.scrollIntoView({ behavior: 'smooth' });

            // Reset flag after animation completes
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 1000);
        }
    };

    // Check location state on mount and scroll to initial section if needed
    useEffect(() => {
        // Check if we arrived from Professional page with a target section
        if (location.state && location.state.targetSection) {
            const sectionId = location.state.targetSection;
            const sectionRefs = {
                'me': meRef,
                'education': educationRef,
                'hobbies': hobbiesRef
            };

            const ref = sectionRefs[sectionId.toLowerCase()];
            if (ref && ref.current) {
                // Set cooldown immediately to prevent observer from overriding active section
                setCooldown();

                // Use timeout to ensure DOM is fully rendered
                setTimeout(() => {
                    // Set the active section (redundant but safe)
                    setActiveSection(sectionId.toLowerCase());

                    // Activate cooldown to prevent flickering
                    setCooldown();

                    // Scroll to section
                    ref.current.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
        // No else block needed as initial state handles default
    }, [location]);

    // Setup intersection observer to detect which section is visible
    useEffect(() => {
        if (!contentMainRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Skip intersection updates if we're in cooldown period
                if (cooldownActiveRef.current) return;

                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const sectionId = entry.target.id;

                        // Update active section state only if not in cooldown period
                        setActiveSection(sectionId);
                    }
                });
            },
            {
                root: contentMainRef.current,
                rootMargin: '0px',
                threshold: 0.6, // Section is considered "active" when 60% visible
            }
        );

        // Observe each section
        const sections = [
            meRef.current,
            educationRef.current,
            hobbiesRef.current
        ].filter(Boolean);

        sections.forEach(section => {
            if (section) observer.observe(section);
        });

        return () => {
            sections.forEach(section => {
                if (section) observer.unobserve(section);
            });

            // Clear any lingering cooldown timer
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, []);

    return (
        <PCLayout
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            activePage={activeSection}
            contentRef={contentMainRef}
            onMenuItemClick={handleMenuItemClick}
        >
            {/* About Me Section with its children */}
            <div id="me" ref={meRef}>
                <Me isDarkMode={isDarkMode} />
            </div>

            <div id="education" ref={educationRef}>
                <Education isDarkMode={isDarkMode} />
            </div>

            <div id="hobbies" ref={hobbiesRef}>
                <Hobbies isDarkMode={isDarkMode} />
            </div>
        </PCLayout>
    );
};

export default PCView;